import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { ASSETS, BRAND, whatsappHref } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#fbf8f0] py-12 lg:py-16 overflow-hidden"
    >
      <img
        src={ASSETS.monogramGold}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute -left-24 -bottom-20 w-[420px] opacity-[0.06]"
      />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <FadeUp>
              <span className="font-body text-[#c56928] text-[13px] tracking-[0.3em] uppercase">
                Contact Us
              </span>
            </FadeUp>
            <h2 className="font-display text-[#07412b] text-[52px] sm:text-7xl lg:text-[110px] leading-[1] mt-2">
              <MaskLineInView>Come stay a while.</MaskLineInView>
            </h2>
            <FadeUp delay={0.1}>
              <p className="font-body font-light text-[#07412b]/75 text-base sm:text-lg mt-4 max-w-lg leading-relaxed">
                Whether you're planning a relaxing getaway, a special celebration
                or a soulful connection with nature — our team is here to curate a
                stay that's truly yours. Reach out any time.
              </p>
            </FadeUp>

            <FadeUp delay={0.14}>
              <div
                data-testid="tariff-note"
                className="mt-6 inline-flex items-start gap-3 rounded-2xl border border-[#ddb128]/50 bg-[#ddb128]/10 px-5 py-4"
              >
                <span className="mt-1 h-2 w-2 rounded-full bg-[#ddb128] shrink-0" aria-hidden="true" />
                <p className="font-body font-light text-[#07412b] text-[13px] sm:text-sm leading-snug">
                  <span className="font-medium">Tariffs &amp; pricing</span> — please reach out to us on WhatsApp for the latest rates and availability.
                </p>
              </div>
            </FadeUp>

            <motion.a
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#07412b] text-[#f7f3e9] px-9 py-5 text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#ddb128] hover:text-[#07412b] transition-colors duration-400"
            >
              <MessageCircle size={20} />
              Message us on WhatsApp
            </motion.a>
          </div>

          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-[#07412b]/12 bg-white/70 p-6 sm:p-8 backdrop-blur-sm">
              <h3 className="font-body text-[#07412b] text-[13px] tracking-[0.25em] uppercase">
                Get in touch
              </h3>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="h-10 w-10 shrink-0 rounded-full bg-[#07412b] text-[#ddb128] flex items-center justify-center">
                    <Phone size={17} />
                  </span>
                  <div>
                    <p className="font-body text-[#07412b]/60 text-[11px] tracking-[0.22em] uppercase">
                      Call / WhatsApp
                    </p>
                    <a
                      href={`tel:${BRAND.phoneDisplay.replace(/\s/g, "")}`}
                      data-testid="contact-phone"
                      className="font-display text-[#07412b] text-xl mt-1 inline-block hover:text-[#c56928] transition-colors"
                    >
                      {BRAND.phoneDisplay}
                    </a>
                    <br />
                    <a
                      href={`tel:${BRAND.phoneDisplay2.replace(/\s/g, "")}`}
                      data-testid="contact-phone-2"
                      className="font-display text-[#07412b] text-xl mt-1 inline-block hover:text-[#c56928] transition-colors"
                    >
                      {BRAND.phoneDisplay2}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-10 w-10 shrink-0 rounded-full bg-[#07412b] text-[#ddb128] flex items-center justify-center">
                    <Mail size={17} />
                  </span>
                  <div>
                    <p className="font-body text-[#07412b]/60 text-[11px] tracking-[0.22em] uppercase">
                      Email
                    </p>
                    <a
                      href={`mailto:${BRAND.email}`}
                      data-testid="contact-email"
                      className="font-display text-[#07412b] text-xl mt-1 inline-block hover:text-[#c56928] transition-colors"
                    >
                      {BRAND.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-10 w-10 shrink-0 rounded-full bg-[#07412b] text-[#ddb128] flex items-center justify-center">
                    <MapPin size={17} />
                  </span>
                  <div>
                    <p className="font-body text-[#07412b]/60 text-[11px] tracking-[0.22em] uppercase">
                      Visit
                    </p>
                    <p className="font-body text-[#07412b] text-base mt-1 leading-snug max-w-xs">
                      {BRAND.address}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
